import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { authApi } from '@/lib/api/auth';

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  companyId: string;
  companyName: string;
  avatarUrl?: string | null;
  /**
   * True when the account was provisioned with a one-time temporary password
   * (admin-created agency owner). The app forces a password reset before any
   * other action while this is set.
   */
  mustChangePassword?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  workspaceId: string | null;
  domainId: string | null;
  isAuthenticated: boolean;

  login: (data: {
    user: AuthUser;
    accessToken: string;
    refreshToken: string;
    workspaceId?: string;
  }) => void;
  logout: () => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setWorkspace: (workspaceId: string) => void;
  setDomain: (domainId: string | null) => void;
  setUser: (user: AuthUser) => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        accessToken: null,
        refreshToken: null,
        workspaceId: null,
        domainId: null,
        isAuthenticated: false,

        login: ({ user, accessToken, refreshToken, workspaceId }) => {
          // Persist to localStorage for the fetchApi client to read
          if (typeof window !== 'undefined') {
            localStorage.setItem('hf-app:access-token', accessToken);
            localStorage.setItem('hf-app:refresh-token', refreshToken);
            if (workspaceId) {
              localStorage.setItem('hf-app:workspace-id', workspaceId);
              document.cookie = `hf-app:workspace-id=${workspaceId}; path=/; max-age=86400; SameSite=Lax`;
            }
            // Sync to cookies so Next.js middleware + API routes can read them
            document.cookie = `hf-app:access-token=${accessToken}; path=/; max-age=86400; SameSite=Lax`;
            // Default to demo mode so all pages work out of the box.
            // User can switch to production mode via the API mode indicator in the sidebar.
            // Auth calls always hit the real backend regardless of mode (skipAuth:true).
            if (!localStorage.getItem('hf-app:api-mode')) {
              localStorage.setItem('hf-app:api-mode', 'demo');
              document.cookie = `hf-app:api-mode=demo; path=/; max-age=86400; SameSite=Lax`;
            } else {
              const existing = localStorage.getItem('hf-app:api-mode') ?? 'demo';
              document.cookie = `hf-app:api-mode=${existing}; path=/; max-age=86400; SameSite=Lax`;
            }
            // Note: hf-app:onboarding-complete is set only after onboarding wizard completes
            // (in onboarding/page.tsx handleComplete). Middleware falls back to backend check
            // if this cookie is absent, so existing users are unaffected after first visit.
          }

          set(
            {
              user,
              accessToken,
              refreshToken,
              workspaceId: workspaceId ?? null,
              isAuthenticated: true,
            },
            false,
            'auth/login',
          );

          // Best-effort: record the browser timezone so security emails render in
          // the user's own zone. This is the single funnel for EVERY login method
          // (SSO, magic-link, password, OTP, 2FA), so redirect flows are covered
          // and the stored zone refreshes on each login. Fire-and-forget.
          if (typeof window !== 'undefined') {
            void authApi.reportTimezone();
          }
        },

        logout: () => {
          // Clear localStorage
          if (typeof window !== 'undefined') {
            localStorage.removeItem('hf-app:access-token');
            localStorage.removeItem('hf-app:refresh-token');
            localStorage.removeItem('hf-app:workspace-id');
            // Drop any in-flight sync banner — its store persists, so a stale
            // domainId would otherwise survive logout and make the global
            // SyncBackgroundBanner poll /v1/onboarding/sync-progress from the
            // /login page (401-loop + "syncing…" flash over the login form).
            localStorage.removeItem('hf-app:sync-banner');
            // Clear auth + onboarding cookies so middleware gates trigger on next visit
            document.cookie = 'hf-app:access-token=; path=/; max-age=0';
            document.cookie = 'hf-app:refresh-token=; path=/; max-age=0';
            document.cookie = 'hf-app:api-mode=; path=/; max-age=0';
            document.cookie = 'hf-app:onboarding-complete=; path=/; max-age=0';
          }

          set(
            {
              user: null,
              accessToken: null,
              refreshToken: null,
              workspaceId: null,
              domainId: null,
              isAuthenticated: false,
            },
            false,
            'auth/logout',
          );

          // Redirect to login
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        },

        setTokens: (accessToken, refreshToken) => {
          if (typeof window !== 'undefined') {
            localStorage.setItem('hf-app:access-token', accessToken);
            localStorage.setItem('hf-app:refresh-token', refreshToken);
            // Keep cookie in sync with refreshed token
            document.cookie = `hf-app:access-token=${accessToken}; path=/; max-age=86400; SameSite=Lax`;
          }

          set({ accessToken, refreshToken }, false, 'auth/setTokens');
        },

        setWorkspace: (workspaceId) => {
          if (typeof window !== 'undefined') {
            localStorage.setItem('hf-app:workspace-id', workspaceId);
          }

          set({ workspaceId }, false, 'auth/setWorkspace');
        },

        setDomain: (domainId) => {
          set({ domainId }, false, 'auth/setDomain');
        },

        setUser: (user) => {
          set({ user }, false, 'auth/setUser');
        },
      }),
      {
        name: 'hf-app:auth-store',
        partialize: (state) => ({
          user: state.user,
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          workspaceId: state.workspaceId,
          domainId: state.domainId,
          isAuthenticated: state.isAuthenticated,
        }),
      },
    ),
    { name: 'AuthStore' },
  ),
);
